import React, { useState, useRef } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { Download, RefreshCw, Trash2, Database, Upload, FileText, AlertTriangle } from 'lucide-react';

const Utilities = () => {
  const { exportData, resetAllPayments, clearAllData, importData } = useFinance();
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleResetPayments = () => {
    const confirmationText = '¿Quieres marcar todos los pagos y el alquiler como pendientes?\n\nEsto es útil para empezar un nuevo mes.';
    if (confirm(confirmationText)) {
      resetAllPayments();
    }
  };

  const handleClearData = () => {
    if (confirm('¿Seguro que quieres borrar todos los datos? Esta acción es irreversible.')) {
      clearAllData();
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setImportMessage('❌ Por favor selecciona un archivo JSON válido.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        setImporting(true);
        setImportMessage('⏳ Importando datos...');

        const success = await importData(jsonData);

        if (success) {
          setImportMessage('✅ Datos importados correctamente');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          setImportMessage('❌ Error al importar los datos. Verifica el formato del archivo.');
        }
      } catch (error) {
        setImportMessage('❌ Error al leer el archivo JSON. Verifica el formato.');
      } finally {
        setImporting(false);
        setTimeout(() => setImportMessage(''), 5000);
      }
    };

    reader.readAsText(file);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="card mb-6 md:mb-8">
      <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
        <h2 className="section-title">
          <span>🔧</span>
          <span>Utilidades</span>
        </h2>
        <Database className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
      </div>

      {/* Primary Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <button
          onClick={exportData}
          className="btn btn-primary"
        >
          <Download className="w-5 h-5" />
          <span className="hidden sm:inline">Exportar</span>
        </button>

        <button
          onClick={handleImportClick}
          disabled={importing}
          className="btn btn-info"
        >
          <Upload className="w-5 h-5" />
          <span className="hidden sm:inline">{importing ? 'Importando...' : 'Importar'}</span>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          className="hidden"
        />

        <button
          onClick={handleResetPayments}
          className="btn btn-info"
        >
          <RefreshCw className="w-5 h-5" />
          <span className="hidden sm:inline">Reiniciar</span>
        </button>

        <button
          onClick={handleClearData}
          className="btn btn-danger"
        >
          <Trash2 className="w-5 h-5" />
          <span className="hidden sm:inline">Borrar Todo</span>
        </button>
      </div>

      {/* Import Message */}
      {importMessage && (
        <div className={`mb-4 p-3 md:p-4 rounded-lg border-2 animate-fade-in ${importMessage.includes('✅')
            ? 'bg-green-50 border-green-200 text-green-800'
            : importMessage.includes('❌')
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
            <span className="text-sm md:text-base font-medium">{importMessage}</span>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
        <div className="flex items-start gap-2 md:gap-3">
          <Database className="w-4 h-4 md:w-5 md:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-800 text-sm md:text-base mb-1">
              💾 Guardado automático
            </h4>
            <p className="text-blue-700 text-xs md:text-sm">
              Tus datos se sincronizan con Firebase en tiempo real.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Utilities;